import { useState } from 'react';
import type { MythingsExpenseCategory, StoreResponse } from '@clarity/contract';
import {
  useCreateMythingsExpense,
  useCreateMythingsPayout,
  useDeleteMythingsExpense,
  useDeleteMythingsPayout,
  useMythingsExpenses,
  useMythingsPayouts,
} from '../queries/index.js';
import { money, shortDate } from './format.js';

/**
 * MyThings owner-entered payouts + expenses (clarity-balance-coverage Req 3, 4).
 * MyThings has no statement, so the owner records what MyThings actually paid
 * them and any costs (marketing / banner / other); the balance card's MyThings
 * estimate reconciles earned − paid − costs against these. A compact panel: the
 * two lists + an inline add form each, with delete. Owner/tenant-scoped reads
 * and writes; a write refreshes the derived summary so the estimate updates.
 *
 * `prefillExpense` lets the balance card hand over a gap ("paid 100 less than
 * estimated") so the owner can file it as an expense with the amount prefilled.
 */
export function MythingsManualEntries({
  store,
  prefillExpenseAmount,
}: {
  store: StoreResponse;
  prefillExpenseAmount?: number | undefined;
}) {
  const currency = store.profile.currency;
  const payouts = useMythingsPayouts(store.id);
  const expenses = useMythingsExpenses(store.id);
  const payoutRows = payouts.data?.status === 200 ? payouts.data.body : [];
  const expenseRows = expenses.data?.status === 200 ? expenses.data.body : [];
  const fmt = (n: number) => money(n, currency);

  return (
    <section className="card mt-manual" aria-label={`MyThings entries — ${store.name ?? 'store'}`}>
      <header className="mt-manual__head">
        <h3>MyThings — payouts &amp; costs you report</h3>
        <p className="mt-manual__hint">
          MyThings doesn&rsquo;t send Clarity a statement, so record what they paid you and any costs.
          Your balance estimate uses these.
        </p>
      </header>

      <div className="mt-manual__cols">
        <div className="mt-manual__col">
          <h4>Payouts received</h4>
          <PayoutForm storeId={store.id} />
          {payoutRows.length === 0 ? (
            <p className="mt-manual__empty">None recorded yet.</p>
          ) : (
            <ul className="mt-manual__list">
              {payoutRows.map((p) => (
                <EntryRow
                  key={p.id}
                  storeId={store.id}
                  entryId={p.id}
                  kind="payout"
                  amount={fmt(p.amount)}
                  label={`Paid ${shortDate(p.paidAt)} · for ${shortDate(p.periodStart)} → ${shortDate(p.periodEnd)}`}
                  note={p.note}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="mt-manual__col">
          <h4>Costs (marketing / banner / other)</h4>
          <ExpenseForm storeId={store.id} {...(prefillExpenseAmount !== undefined && { prefillAmount: prefillExpenseAmount })} />
          {expenseRows.length === 0 ? (
            <p className="mt-manual__empty">None recorded yet.</p>
          ) : (
            <ul className="mt-manual__list">
              {expenseRows.map((e) => (
                <EntryRow
                  key={e.id}
                  storeId={store.id}
                  entryId={e.id}
                  kind="expense"
                  amount={fmt(e.amount)}
                  label={`${labelOf(e.category)} · ${shortDate(e.periodStart)} → ${shortDate(e.periodEnd)}`}
                  note={e.note}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function labelOf(category: MythingsExpenseCategory): string {
  return category === 'marketing' ? 'Marketing' : category === 'banner' ? 'Banner' : 'Other';
}

/** The current month as `[firstDay, lastDay]` ISO instants, for a sensible default period. */
function currentMonthBounds(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59, 999));
  return { start: start.toISOString(), end: end.toISOString() };
}

function toIso(dateInput: string): string {
  // `<input type=date>` gives `YYYY-MM-DD`; treat as a UTC day.
  const t = Date.parse(`${dateInput}T00:00:00.000Z`);
  return Number.isFinite(t) ? new Date(t).toISOString() : new Date().toISOString();
}

function dayValue(iso: string): string {
  return iso.slice(0, 10);
}

function PayoutForm({ storeId }: { storeId: string }) {
  const create = useCreateMythingsPayout(storeId);
  const bounds = currentMonthBounds();
  const [periodStart, setStart] = useState(dayValue(bounds.start));
  const [periodEnd, setEnd] = useState(dayValue(bounds.end));
  const [paidAt, setPaidAt] = useState(dayValue(new Date().toISOString()));
  const [amount, setAmount] = useState('');

  const n = Number(amount);
  const valid = Number.isFinite(n) && n >= 0 && amount.trim() !== '';

  return (
    <form
      className="mt-manual__form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || create.isPending) return;
        create.mutate(
          { params: { id: storeId }, body: { periodStart: toIso(periodStart), periodEnd: toIso(periodEnd), paidAt: toIso(paidAt), amount: n, note: null } },
          { onSuccess: () => setAmount('') },
        );
      }}
    >
      <label>Amount<input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required /></label>
      <label>Paid on<input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} /></label>
      <label>For from<input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} /></label>
      <label>to<input type="date" value={periodEnd} onChange={(e) => setEnd(e.target.value)} /></label>
      <button type="submit" disabled={!valid || create.isPending}>Add payout</button>
    </form>
  );
}

function ExpenseForm({ storeId, prefillAmount }: { storeId: string; prefillAmount?: number | undefined }) {
  const create = useCreateMythingsExpense(storeId);
  const bounds = currentMonthBounds();
  const [periodStart, setStart] = useState(dayValue(bounds.start));
  const [periodEnd, setEnd] = useState(dayValue(bounds.end));
  const [category, setCategory] = useState<MythingsExpenseCategory>('marketing');
  const [amount, setAmount] = useState(prefillAmount ? String(prefillAmount) : '');

  const n = Number(amount);
  const valid = Number.isFinite(n) && n >= 0 && amount.trim() !== '';

  return (
    <form
      className="mt-manual__form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || create.isPending) return;
        create.mutate(
          { params: { id: storeId }, body: { periodStart: toIso(periodStart), periodEnd: toIso(periodEnd), category, amount: n, note: null } },
          { onSuccess: () => setAmount('') },
        );
      }}
    >
      <label>Amount<input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required /></label>
      <label>
        Type
        <select value={category} onChange={(e) => setCategory(e.target.value as MythingsExpenseCategory)}>
          <option value="marketing">Marketing</option>
          <option value="banner">Banner</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label>From<input type="date" value={periodStart} onChange={(e) => setStart(e.target.value)} /></label>
      <label>to<input type="date" value={periodEnd} onChange={(e) => setEnd(e.target.value)} /></label>
      <button type="submit" disabled={!valid || create.isPending}>Add cost</button>
    </form>
  );
}

function EntryRow({
  storeId,
  entryId,
  kind,
  amount,
  label,
  note,
}: {
  storeId: string;
  entryId: string;
  kind: 'payout' | 'expense';
  amount: string;
  label: string;
  note: string | null;
}) {
  const delPayout = useDeleteMythingsPayout(storeId);
  const delExpense = useDeleteMythingsExpense(storeId);
  const del = kind === 'payout' ? delPayout : delExpense;
  return (
    <li className="mt-manual__row">
      <span className="num mt-manual__amt">{amount}</span>
      <span className="mt-manual__meta">
        {label}
        {note ? <span className="mt-manual__note"> · {note}</span> : null}
      </span>
      <button
        type="button"
        className="mt-manual__del"
        aria-label="Delete entry"
        disabled={del.isPending}
        onClick={() => del.mutate({ params: { id: storeId, entryId }, body: {} })}
      >
        ✕
      </button>
    </li>
  );
}
